import type { ButtonProps } from "@/types";
import { useResponsive } from "@h/useResponsive";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@u/cn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { buttonVariants } from "./button-variants";

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      design,
      size,
      asChild = false,
      to,
      external,
      label,
      loading,
      icon: Icon,
      iconPosition = "left",
      isActive,
      floatingLabel,
      floatingPosition,
      responsiveLabel,
      children,
      state,
      isDraggable,
      isFocused,
      type = "button", // 1. Blindaje contra micro-reloads
      ...props
    },
    ref,
  ) => {
    const isExternal = to?.startsWith("http") || external;
    const { isMobileOrLandscape } = useResponsive();

    // Breakpoint at which a `responsiveLabel` button reveals its text. `true`
    // keeps the legacy `sm` behavior. Static class strings so Tailwind detects.
    const rlBp = responsiveLabel === true ? "sm" : responsiveLabel || undefined;
    const rlLabelCls = {
      sm: "hidden sm:inline",
      md: "hidden md:inline",
      lg: "hidden lg:inline",
      xl: "hidden xl:inline",
      "2xl": "hidden 2xl:inline",
    };
    const rlSquareCls = {
      sm: "max-sm:aspect-square max-sm:w-auto max-sm:px-0",
      md: "max-md:aspect-square max-md:w-auto max-md:px-0",
      lg: "max-lg:aspect-square max-lg:w-auto max-lg:px-0",
      xl: "max-xl:aspect-square max-xl:w-auto max-xl:px-0",
      "2xl": "max-2xl:aspect-square max-2xl:w-auto max-2xl:px-0",
    };

    // El contenido visual siempre es el mismo
    const content = (
      <span className="flex items-center justify-center gap-2">
        {loading ? (
          <Loader2 className="animate-spin" size={size === "sm" || size === "xs" || size === "icon-sm" ? 14 : 18} />
        ) : (
          <>
            {Icon && iconPosition === "left" && (
              <Icon size={size === "sm" || size === "xs" || size === "icon-sm" ? 14 : 18} />
            )}
            {label &&
              (rlBp ? (
                <span className={rlLabelCls[rlBp]}>{label}</span>
              ) : (
                <span>{label}</span>
              ))}
            {children}
            {Icon && iconPosition === "right" && (
              <Icon size={size === "sm" || size === "xs" || size === "icon-sm" ? 14 : 18} />
            )}
          </>
        )}
      </span>
    );

    // a11y: an icon-only button (icon, no visible label/children) has no
    // accessible name. Derive one from floatingLabel/label so screen readers
    // announce it — without forcing every call site to pass aria-label.
    const ariaFromProps = (props as { "aria-label"?: string })["aria-label"];
    // When responsiveLabel collapses the label below `sm`, the text is hidden
    // (display:none) so it gives no accessible name — treat it as icon-only.
    // `!label && children` covers the HUD pattern where the text lives in a
    // `<span className="hidden lg:inline">` child: visually a label on desktop,
    // display:none (⇒ no accessible name) on the viewports axe scans as mobile.
    // Harmless when the child IS visible — the derived name is that same text.
    const isIconOnly = (!!Icon && !label) || (!!rlBp && !!Icon);
    const derivedAriaLabel =
      ariaFromProps ??
      (typeof floatingLabel === "string" ? floatingLabel : undefined) ??
      (typeof label === "string" ? label : undefined);

    // Props que comparten todos los elementos (clases y estados)
    const commonProps = {
      className: cn(
        buttonVariants({ design, size, className }),
        isDraggable && "cursor-grab active:cursor-grabbing",
        // icon-only square below the breakpoint, full label + padding above it
        rlBp && Icon && rlSquareCls[rlBp],
      ),
      "data-state":
        state ?? (isActive ? "active" : isFocused ? "focused" : undefined),
      ...props,
      ...(isIconOnly && derivedAriaLabel
        ? { "aria-label": derivedAriaLabel }
        : {}),
    };

    let mainElement;

    if (asChild) {
      mainElement = (
        <Slot ref={ref as React.Ref<HTMLElement>} {...commonProps}>
          {children || content}
        </Slot>
      );
    } else if (to) {
      if (isExternal) {
        mainElement = (
          <a
            href={to}
            target="_blank"
            rel="noreferrer"
            ref={ref as React.Ref<HTMLAnchorElement>}
            {...(commonProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            {content}
          </a>
        );
      } else {
        mainElement = (
          <Link
            to={to}
            ref={ref as React.Ref<HTMLAnchorElement>}
            {...(commonProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            {content}
          </Link>
        );
      }
    } else {
      mainElement = (
        <button
          type={type}
          disabled={loading || props.disabled}
          ref={ref as React.Ref<HTMLButtonElement>}
          {...commonProps}
        >
          {content}
        </button>
      );
    }

    // A `responsiveLabel` button is icon-only below its breakpoint, so it needs
    // a tooltip there — fall back to `label` instead of making every call site
    // repeat it as `floatingLabel`. The `rlHideTip` class below already drops
    // the tooltip once the text is visible.
    // Every icon-only button gets a hover tooltip, derived from whatever name
    // it already carries (aria-label / label / floatingLabel) — no call site
    // has to remember to pass `floatingLabel` a second time. Buttons with a
    // visible label are unaffected.
    const iconOnly = isIconOnly || (size === "icon" && !label);
    const tipLabel =
      floatingLabel ??
      (rlBp && Icon ? label : undefined) ??
      (iconOnly ? derivedAriaLabel : undefined);
    // Icon buttons usually sit in horizontal clusters (row actions, toolbars),
    // so a side tooltip would cover the neighbor — default is always bottom;
    // explicit `floatingPosition` still wins (e.g. "top" near the viewport edge).
    const tipSide = floatingPosition ?? "bottom";

    if (tipLabel && !loading && !isMobileOrLandscape) {
      // When the label collapses via `responsiveLabel`, only surface the
      // tooltip while the button is icon-only — hide it once the label shows.
      const rlHideTip = {
        sm: "sm:hidden",
        md: "md:hidden",
        lg: "lg:hidden",
        xl: "xl:hidden",
        "2xl": "2xl:hidden",
      };
      return (
        <Tooltip>
          <TooltipTrigger asChild>{mainElement}</TooltipTrigger>
          <TooltipContent
            side={tipSide}
            align="center"
            className={rlBp ? rlHideTip[rlBp] : undefined}
          >
            {tipLabel}
          </TooltipContent>
        </Tooltip>
      );
    }

    return mainElement;
  },
);

Button.displayName = "Button";

export { Button as default };
